import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    SidebarLayout as Layout,
    isOfTypeNavEntryGroup,
    ToolsAndChangelogButtons,
    type SidebarEntry,
} from '@starjack/layout';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useSidebarItems } from '@/layouts/sidebar/config';
import type {
    B2bNavEntryGroup,
    B2bNavEntryItem,
} from '@/layouts/sidebar/config';
import { AccountPopover } from '@/components/AccountPopover/AccountPopover';
import { LanguageToggle } from '@/components/LanguageSwitcher/LanguageToggle';
import { isB2B } from '@/constants';
import { Avatar, Box } from '@mui/material';

export { SIDE_NAV_WIDTH } from '@starjack/layout';

function toPackageEntries(
    entries: (B2bNavEntryGroup | B2bNavEntryItem)[]
): SidebarEntry[] {
    return entries.map((e) => {
        if (isOfTypeNavEntryGroup(e as SidebarEntry)) {
            const group = e as B2bNavEntryGroup;
            return {
                ...group,
                entries: group.entries.map((entry) => ({
                    ...entry,
                    permissionsRequired: entry.permissionsRequired.map(String),
                })),
            };
        }
        const item = e as B2bNavEntryItem;
        return {
            ...item,
            permissionsRequired: item.permissionsRequired.map(String),
        };
    });
}

export function useResolvedSidebarEntries(): SidebarEntry[] {
    const sidebarItems = useSidebarItems();
    return useMemo(() => toPackageEntries(sidebarItems), [sidebarItems]);
}

function AccountPopoverSlot() {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const { liftOperatorData } = useAuthentication();
    return (
        <>
            <Avatar
                component='button'
                aria-label='Konto-Menü öffnen'
                aria-haspopup='menu'
                color='secondary'
                onClick={() => setOpen(true)}
                ref={anchorRef}
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    height: 48,
                    width: 48,
                    fontSize: '1.25rem',
                })}
            >
                {liftOperatorData?.skiOperator?.[0]?.toUpperCase()}
            </Avatar>
            <AccountPopover
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
            />
        </>
    );
}

export const SidebarLayout: FunctionComponent<PropsWithChildren<unknown>> = ({
    children,
}) => {
    const {
        actions: { resetPageData },
        state: { pageDescription, explanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');
    const entries = useResolvedSidebarEntries();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const head = document.getElementsByTagName('head')[0];
        if (!head) return;
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://public.produktly.com/js/main.js';
        script.setAttribute('id', 'produktlyScript');
        script.dataset.clientToken =
            '88e965f16f7e9010e728aa2101cafcfd4ad02fc30dacc5769391c704fbf9cbe469e8e3414a150763c050fb8b0210248b59035881fdcc1d5c019f95fe431c52233264dde499a8c428a3f968dde988f6882f8e59b04be22427b3fb60fc746fe9218dbfc04a';
        head.appendChild(script);
    }, []);

    const rightSlot = (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 2 }}>
            {isB2B && <LanguageToggle />}
            <ToolsAndChangelogButtons />
            <AccountPopoverSlot />
        </Box>
    );

    return (
        <Layout
            entries={entries}
            logo={{ src: '/favicon/favicon-32x32.png', alt: 'Starjack Logo' }}
            appName='starjack B2B'
            pageDescription={pageDescription}
            explanationVideo={explanationVideo}
            resetPageData={resetPageData}
            rightSlot={rightSlot}
        >
            {children}
        </Layout>
    );
};
